import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import type { BillingSummaryDto } from "@/shared/api/types";
import { apiGet } from "@/shared/api/client";
import { queryKeys } from "@/shared/api/query-keys";
import { useTenantId } from "@/shared/auth/session";
import { BILLING_POLL_MS, BILLING_POLL_WINDOW_MS } from "@/shared/constants/limits";

/**
 * `GET /billing` — the one read behind the Subscription tab, the shell's payment banners and the suspended
 * interstitial, so it lives in `shared/` rather than in the settings module (rules/01 §1).
 *
 * It needs `billing:view`, which is Owner-only by preset (ADR-0006), so every caller passes `enabled` from
 * the permission check: an Admin never fires it and never sees a 403.
 *
 * Nothing on this read is decided by the portal. A payment becomes real when the gateway's signed webhook
 * lands and the worker's `applyPaidOrder` writes it (ADR-0015 §3), which is why paying opens a hosted page
 * and then starts `watchForChange` — a bounded polling window that ends by itself, never an interval left
 * running. The quantities and limits also come from the 30-second entitlement cache, so they can lag the
 * order by that much again (runbooks/billing.md).
 */
export function useBillingSummaryQuery(enabled: boolean) {
  const tenantId = useTenantId();
  const [pollUntil, setPollUntil] = useState<number | null>(null);

  useEffect(() => {
    if (pollUntil === null) return;
    const timer = setTimeout(() => setPollUntil(null), Math.max(0, pollUntil - Date.now()));
    return () => clearTimeout(timer);
  }, [pollUntil]);

  const query = useQuery({
    queryKey: queryKeys.tenant.billing.summary(tenantId),
    queryFn: ({ signal }) => apiGet<BillingSummaryDto>("/billing", undefined, signal),
    enabled,
    refetchInterval: pollUntil === null ? false : BILLING_POLL_MS,
  });

  // Stable, so an effect that opens the window once per episode (`useStaleRenewal`) can name it as a dependency.
  const watchForChange = useCallback(() => setPollUntil(Date.now() + BILLING_POLL_WINDOW_MS), []);

  return { ...query, watching: pollUntil !== null, watchForChange };
}
